import { Trans, useTranslation } from 'react-i18next'

import { SeoHead } from '@/shared/components/SeoHead'
import { useLocaleStore } from '@/shared/store/localeStore'

import { HelpArticleSection } from '../components/HelpArticleSection'
import { HelpContactBlock } from '../components/HelpContactBlock'
import { HelpHero } from '../components/HelpHero'
import { HelpLayout } from '../components/HelpLayout'
import { HelpRelatedLinks } from '../components/HelpRelatedLinks'
import { buildHelpSchema } from '../components/HelpSchemaJsonLd'
import { getHelpCategoryByKey, type HelpCategoryKey } from '../data/helpCategories'

const KEY: HelpCategoryKey = 'stores'

/**
 * Guía para tiendas (`/help/tiendas`): todo lo que la app hace por una tienda o local. 🔴 Se
 * mantiene al día con cada cambio de las funciones de tienda (CLAUDE.md, hot-list).
 */
export default function HelpStoresPage() {
  const { t } = useTranslation()
  const locale = useLocaleStore((s) => s.locale)
  const category = getHelpCategoryByKey(KEY)
  const accent = category?.accentColor ?? 'green'

  const headline = t('help.stores.title')
  const description = t('help.stores.lead')

  const schema = buildHelpSchema({
    kind: 'article',
    headline,
    description,
    key: KEY,
    locale,
  })

  return (
    <HelpLayout currentKey={KEY}>
      <SeoHead
        title={`${headline} | Match&Play`}
        description={description}
        routeId="help-category:stores"
        jsonLd={schema}
      />

      <HelpHero
        eyebrow={t('help.categories.stores.title')}
        title={headline}
        subtitle={description}
        size="md"
      />

      <HelpArticleSection title={t('help.stores.sections.what.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.what.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.signup.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.signup.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.venues.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.venues.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.editing.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.editing.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.bookings.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.bookings.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.agenda.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.agenda.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.events.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.events.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.series.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.series.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.manageEvent.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.manageEvent.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.vip.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.vip.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.followers.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.followers.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.banners.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.banners.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.stats.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.stats.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.notifications.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.notifications.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.settings.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.settings.body" />
      </HelpArticleSection>

      <HelpArticleSection title={t('help.stores.sections.limits.title')} accent={accent}>
        <Trans i18nKey="help.stores.sections.limits.body" />
      </HelpArticleSection>

      <HelpContactBlock />
      <HelpRelatedLinks currentKey={KEY} />
    </HelpLayout>
  )
}
